import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import TopNavigation from '../components/TopNavigation';
import { authService, ForgotPasswordRequest } from '@/services/authService';

interface ForgotPasswordPageProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
}

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onToggleMenu
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  const themeClasses = isDarkMode ? 'text-white' : 'bg-white text-gray-900';

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);

    // Clear errors when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate email
      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }

      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        throw new Error('Please enter a valid email address');
      }

      const forgotRequest: ForgotPasswordRequest = {
        email: email.trim()
      };

      const response = await authService.forgotPassword(forgotRequest);

      setSuccess(response.message);
      setEmail(''); // Clear form

    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send password reset email';

      // Handle rate limiting specifically
      if (errorMessage.includes('Too many')) {
        setError('Too many password reset requests. Please wait before trying again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? '#1C1C1E' : 'bg-white' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
      />

      {/* Main Content */}
      <div className="w-full flex flex-col px-8 lg:px-16 xl:px-24 py-8 pb-24 sm:pb-20">
        <div className="w-full max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-regular mb-2">Reset Your Password</h1>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Enter your email address and we'll send you a link to reset your password
            </p>
          </div>

          {/* Reset Form */}
          <div className="max-w-md mx-auto">
            <div className={`p-8 rounded-lg border border-gray-300 ${
              isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-gray-50 border-gray-200'
            }`}>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="mb-6 p-6 bg-green-50 border-2 border-green-200 rounded-xl text-center">
                  <div className="flex flex-col items-center space-y-4">
                    {/* Email Icon */}
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>

                    {/* Success Text */}
                    <div>
                      <h3 className="text-xl font-semibold text-green-800 mb-2">Email Sent!</h3>
                      <p className="text-green-700 text-sm">
                        {success}
                      </p>
                    </div>

                    {/* Additional Instructions */}
                    <div className="bg-white border border-green-200 rounded-lg p-4 w-full">
                      <h4 className="font-medium text-green-800 mb-2">Next Steps:</h4>
                      <ol className="text-sm text-green-700 space-y-1 text-left">
                        <li>1. Check your email inbox (and spam folder)</li>
                        <li>2. Click the password reset link in the email</li>
                        <li>3. Enter your new password</li>
                        <li>4. Sign in with your new password</li>
                      </ol>
                    </div>
                  </div>
                </div>
              )}

              {/* Form - Hidden when success message is shown */}
              {!success && (
                <>
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    <div>
                      <label htmlFor="email" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={handleEmailChange}
                        required
                        className={`w-full px-3 py-2 rounded-full border ${
                          isDarkMode
                            ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                            : 'bg-gray-50 border-blue-600 text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Enter your email address"
                        disabled={isLoading}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white transition-colors ${
                        isLoading
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                      }`}
                    >
                      {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </form>
                </>
              )}

              {/* Back to Login */}
              <div className="mt-6 text-center">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Remember your password?{' '}
                  <button
                    onClick={() => onNavigateToLogin?.()}
                    className={`font-medium cursor-pointer hover:underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                  >
                    Back to Sign In
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
